/**
 * A tela de WhatsApp (histórias 8.1, 8.3 e 8.4).
 *
 *     /whatsapp?conversa=<uuid>    a lista de conversas e a conversa aberta (FR-37, UX-DR7)
 *
 * Ela é filha de `rotaDaAplicacao`, e por isso herda a guarda de sessão do layout — registrada uma
 * vez lá, ela vale para toda rota interna.
 *
 * **O caminho já existia como placeholder**, desde a 1.4, e virou tela de verdade na 8.1 —
 * `CAMINHOS.whatsapp` está em `COM_TELA_PROPRIA` (`rotas/telas.ts`) desde então, e nada disso muda
 * aqui.
 *
 * **O que ela monta mudou na 8.3.** Até a 8.2 o corpo era o cartão do próprio número, e a lista de
 * conversas era a promessa escrita neste arquivo. Agora o corpo é a conversa; o cartão continua
 * **alcançável pela própria tela**, atrás de um botão na barra do topo — ele é a porta de entrada
 * de tudo isto (sem número conectado não há conversa a exibir, e o `409` do envio manda conectar), e
 * quem precisa dele já está aqui.
 *
 * **A 11.12 também não mudou esta rota, e o motivo é o mesmo da 8.4.** O desenho do WhatsApp Web,
 * o "Abrir negócio" (o modal da 11.4 entra pelo `?negocio=` do layout, sem nada aqui), a faixa
 * "Desconectado" e o celular abrindo na lista acontecem dentro de `<Conversas/>`. O que a rota
 * resolve — qual conversa está aberta — continua igual: no computador ela é o chat da direita; no
 * celular, a conversa que o toque na lista abre.
 *
 * **A 8.4 não mudou nada desta rota, e a ausência é informação.** A mídia é da conversa, e a
 * conversa já estava aqui: anexar, gravar, ouvir, abrir e baixar acontecem dentro de `<Conversas/>`,
 * e os bytes saem por uma rota do serviço que o próprio `<img>`/`<audio>`/`<video>` busca. Não há
 * `?midia=` na URL, e não deveria haver: o "onde eu estou" de quem trabalha continua sendo a
 * conversa.
 *
 * **E a 8.5 também não mudou nada aqui, por uma razão diferente.** O e-mail arquivado por cópia
 * (FR-42) não é uma tela: ele é um cartão dentro da ficha do **Negócio**, porque é do histórico do
 * Negócio que o requisito fala. O `GET /emails/nao-vinculados` — o e-mail que citou um código
 * inexistente — também não ganha item de navegação lateral, pelo mesmo critério de
 * `envios_com_falha`: ele é sinal de **operação**, e uma tela a mais seria escopo que ninguém
 * pediu.
 *
 * **A conversa aberta mora na URL, e não num `useState`** — molde de `funil.tsx`. Ela é o "onde eu
 * estou" de quem trabalha: um link, um "voltar" do navegador, uma aba recarregada, e sobretudo a
 * virada dos 768px do shell, que troca a árvore e remonta o `<Outlet/>`. Em estado local ela se
 * perderia nas quatro.
 *
 * **A rota resolve o fallback, e a tela desenha.** É a mesma divisão de `funil.tsx`: aqui se decide
 * *qual* conversa está aberta (a da URL, se ela existir de verdade; senão a mais recente), e o
 * componente recebe a decisão pronta.
 *
 * **E o fallback é dito, e não silencioso** — nisto ela difere de `funil.tsx`, e a diferença é do
 * dado. Um `?funil=` que não existe cai no padrão do perfil e ninguém perde nada: o quadro é o
 * mesmo trabalho. Uma conversa é **de alguém**: abrir outra em silêncio poria o nome de outro
 * cliente no cabeçalho, com o histórico dele na tela, para quem clicou num link achando que ia para
 * a conversa certa. A rota informa ao componente que o link não foi encontrado, e ele avisa.
 *
 * **Os dois motivos são indistinguíveis daqui, e a frase diz os dois.** `GET /conversas` devolve as
 * mais recentes com teto e sem paginação, então um `?conversa=` legítimo pode estar **fora** da
 * lista por ser antigo; e a RLS pode simplesmente não entregar aquela conversa a quem está olhando
 * (FR-39). Distinguir exigiria uma segunda chamada por link aberto, e as duas terminam na mesma
 * ação de quem lê: procurar a conversa na lista.
 *
 * **`GET /conversas` é chamado aqui, e não no componente**, pela mesma razão de `useFunis` em
 * `funil.tsx`: é a rota que precisa da lista para resolver a escolha, e uma segunda consulta no
 * componente seria a mesma chave do react-query pedida duas vezes — dedupe hoje, e duas fontes de
 * verdade no dia em que os parâmetros divergirem.
 *
 * **O título é publicado pela rota**, e não deixado para o item de navegação. O `<h1>` do shell sai
 * do item da rota aberta, e publicá-lo aqui é o que mantém o cabeçalho certo mesmo se o item mudar
 * de forma. O texto é o **mesmo** do item de menu, escrito uma vez em `navegacao.ts`.
 */

import { createRoute, useLocation, useRouter } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'

import { Conversas } from '@/modulos/comunicacao/Conversas'
import { useConversas } from '@/modulos/comunicacao/api'
import { useDefinirTitulo, useEhCelular } from '@/shell/Shell'
import { CAMINHOS, TITULO_DO_WHATSAPP } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/** O que a URL desta tela carrega. Os campos são opcionais: `/whatsapp` cru é válido. */
export interface BuscaDoWhatsapp {
  /** O `uuid` da conversa aberta. Ausente é "a mais recente". */
  conversa?: string
  /**
   * 13.14c (DW-380): o painel "Dados do contato" aberto **no celular**, como entrada do histórico —
   * o "voltar" do Android o fecha e a conversa fica. No computador ele não vale (o painel é estado
   * da tela, como antes) e sai da URL.
   */
  dados?: 1
}

/*
 * A marca de "esta entrada foi empurrada por esta aba ao abrir o painel" (13.14c): só com ela o
 * "← Conversa" volta no histórico (a entrada de baixo é a conversa sem o painel); sem ela — a
 * recarga que perdeu a pilha, o modal do Negócio que trocou a entrada —, ele troca a entrada.
 */
declare module '@tanstack/history' {
  interface HistoryState {
    dadosEmpurrados?: boolean
  }
}

function TelaDeWhatsapp() {
  useDefinirTitulo(TITULO_DO_WHATSAPP)

  // A anotação é explícita porque `useSearch` da própria rota que a declara vê o tipo em
  // construção, e a inferência cai numa união com o `{}` do layout pai. `validateSearch` continua
  // sendo o dono do formato.
  const { conversa }: BuscaDoWhatsapp = rotaDeWhatsapp.useSearch()
  const navegar = rotaDeWhatsapp.useNavigate()
  const roteador = useRouter()
  /* O `dados` e a marca saem da **mesma** leitura da localização: no "voltar", a busca validada da
     rota chega um quadro depois do `state` da entrada nova, e misturar as duas fontes faria a
     entrada de baixo parecer um `dados` sem marca (e a tela o reempurraria). */
  const dadosBrutos = useLocation({ select: (local) => textoDosDados(local.search) })
  const empurradaAqui = useLocation({ select: (local) => local.state.dadosEmpurrados === true })
  const dados = dadosBrutos === undefined ? undefined : dadosValidos(JSON.parse(dadosBrutos))
  const ehCelular = useEhCelular()
  const consulta = useConversas()

  const conversas = consulta.data ?? []
  // A da URL vence — mas só se ela existir de verdade. Um `?conversa=` de uma conversa que a lista
  // não trouxe cairia num chat vazio sem explicação; a mais recente é o padrão honesto no
  // computador (no celular a tela abre na lista desde a 11.12, e o toque escolhe).
  const daUrl = conversas.find((item) => item.id === conversa)
  const aberta = daUrl ?? conversas[0]
  /*
   * O link apontou para uma conversa que esta tela não carregou, e ela abriu outra — a pessoa
   * precisa saber. A condição espera a lista **responder** (`!isLoading`) e exige que ela tenha
   * alguma conversa: sem isso, o primeiro quadro de renderização de todo acesso com `?conversa=`
   * acenderia o aviso, e a lista vazia acenderia o aviso errado (o certo ali é "nenhuma conversa
   * ainda", que o componente já desenha).
   */
  const linkNaoEncontrado =
    conversa !== undefined && daUrl === undefined && !consulta.isLoading && conversas.length > 0

  /*
   * **Escrever de volta na URL é `replace`, e não `push`** — a diferença é o botão "voltar".
   *
   * Esta escrita não é uma escolha de quem trabalha: é a tela dizendo qual conversa ela abriu
   * sozinha. Empilhada no histórico, o "voltar" devolveria a mesma tela com o parâmetro que acabou
   * de ser corrigido, e a correção aconteceria de novo — um laço do qual só dá para sair fechando a
   * aba. Escolher **outra** conversa na lista empilha (é `push`, logo abaixo), porque aquilo é uma
   * virada de contexto que todo mundo espera desfazer com o "voltar".
   *
   * O efeito só dispara quando os dois discordam, e a lista já respondeu: durante o carregamento
   * `aberta` é `undefined` e nada é escrito.
   */
  useEffect(() => {
    if (aberta !== undefined && aberta.id !== conversa) {
      //: Sem `dados` (13.14c): o painel nunca abre sobre a conversa que a tela escolheu sozinha.
      void navegar({ search: { conversa: aberta.id }, replace: true })
    }
  }, [aberta, conversa, navegar])

  /*
   * 13.14c (DW-380): o `dados` que chegou pela URL sem ter sido empurrado aqui (recarga, link,
   * avançar depois de a pilha se perder). No celular a entrada é refeita em duas — a conversa sem o
   * painel por baixo e o painel por cima, marcado —, para o "voltar" fechar o painel em vez de sair
   * do WhatsApp; no computador, onde ele não vale, ele sai da URL.
   */
  const conversaDaUrl = daUrl?.id
  const corrigindo = useRef(false)
  useEffect(() => {
    if (dadosBrutos === undefined || conversaDaUrl === undefined || corrigindo.current) {
      return
    }
    if (dados === 1 && empurradaAqui && ehCelular) {
      return
    }
    //: Uma correcao por vez: as duas navegacoes mudam a URL, e o efeito roda de novo no meio.
    corrigindo.current = true
    void (async () => {
      try {
        await navegar({ search: { conversa: conversaDaUrl }, replace: true })
        if (dados === 1 && ehCelular) {
          await navegar({
            search: { conversa: conversaDaUrl, dados: 1 },
            state: (anterior) => ({ ...anterior, dadosEmpurrados: true }),
          })
        }
      } finally {
        corrigindo.current = false
      }
    })()
  }, [dadosBrutos, dados, empurradaAqui, conversaDaUrl, ehCelular, navegar])

  const abrirDadosNaUrl = useCallback(
    async (conversaId: string) => {
      //: O toque duplo antes de a tela redesenhar não empurra a mesma entrada duas vezes.
      if (dados === 1 && conversaId === conversa) {
        return
      }
      if (conversaId !== conversa) {
        await navegar({ search: { conversa: conversaId } })
      }
      await navegar({
        search: { conversa: conversaId, dados: 1 },
        state: (anterior) => ({ ...anterior, dadosEmpurrados: true }),
      })
    },
    [dados, conversa, navegar],
  )

  const fecharDadosNaUrl = useCallback(() => {
    if (dados !== 1) {
      return
    }
    if (empurradaAqui) {
      roteador.history.back()
      return
    }
    void navegar({ search: { conversa }, replace: true })
  }, [dados, empurradaAqui, roteador, conversa, navegar])

  return (
    <Conversas
      conversas={conversas}
      carregando={consulta.isLoading}
      erro={consulta.isError ? consulta.error : null}
      aoRecarregar={() => {
        void consulta.refetch()
      }}
      conversaAberta={aberta}
      linkNaoEncontrado={linkNaoEncontrado}
      aoEscolherConversa={(conversaId) => {
        void navegar({ search: { conversa: conversaId } })
      }}
      conversaNaUrl={conversa !== undefined}
      dadosNaUrl={ehCelular && dados === 1 && empurradaAqui && daUrl !== undefined}
      aoAbrirDadosNaUrl={(conversaId) => {
        void abrirDadosNaUrl(conversaId)
      }}
      aoFecharDadosNaUrl={fecharDadosNaUrl}
    />
  )
}

/** O `dados` cru da busca, como texto (para a leitura comparar por valor), ou ausente. */
function textoDosDados(busca: object): string | undefined {
  const bruto = (busca as { dados?: unknown }).dados
  return bruto === undefined ? undefined : JSON.stringify(bruto)
}

/** O `dados` da busca: o roteador lê a URL como JSON, então `1`, `"1"` e `true` valem; o resto não. */
function dadosValidos(bruto: unknown): 1 | undefined {
  return bruto === 1 || bruto === '1' || bruto === true ? 1 : undefined
}

export const rotaDeWhatsapp = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.whatsapp,
  component: TelaDeWhatsapp,
  /**
   * A URL é entrada de quem chega, e não só do que a tela escreveu: alguém edita a barra de
   * endereço, um link antigo circula, o histórico traz um valor de outra versão da tela. O que não
   * for texto cai fora **aqui**, e a tela recebe sempre um estado que ela sabe desenhar.
   *
   * O `uuid` **não** é conferido contra a lista aqui: ela só existe depois da resposta do serviço, e
   * um validador que dependesse dela seria um validador que às vezes não valida. Quem decide é a
   * tela, comparando com as conversas que chegaram — e um `id` que não está lá cai na mais recente,
   * em vez de num chat vazio.
   */
  validateSearch: (parametros: Record<string, unknown>): BuscaDoWhatsapp => {
    const escolhida = typeof parametros.conversa === 'string' ? parametros.conversa.trim() : ''
    const dados = dadosValidos(parametros.dados)
    return {
      conversa: escolhida === '' ? undefined : escolhida,
      ...(dados === undefined ? {} : { dados }),
    }
  },
})
